import React, {useContext, useEffect, useMemo, useRef} from "react";
import {AuthContext, TAuthConfig} from "react-oauth2-code-pkce";
import {useNavigate} from "react-router-dom";
import { TTokenData } from 'react-oauth2-code-pkce/dist/types';

interface UKPBAuthProviderContext {
    LogIn: () => void;
    LogOut: () => void;
    LogInSuccess: () => void;
    RedirectError: () => void;
    ClearLocalStore?: any;
    loggingIn?: any;
    loggingOut?: any;
    preAuthUrl?: string;

    token: string;
    error: string | null;
    loginInProgress: boolean;
    tokenData?: TTokenData;
    idToken?: string;
    idTokenData?: TTokenData;
}

const authScopes = [
    'openid',
    'offline_access',
    'api.client.read',
    'api.client.write',
    'auth.account.read',
    'auth.account.write',
    'files.client.user_files.read',
    'files.client.user_files.write',
    'device_id'
];

export const authConfig: TAuthConfig = {
    clientId: process.env.REACT_APP_AUTH_CLIENT_ID || 'NotSet',
    authorizationEndpoint: `${process.env.REACT_APP_AUTH_HOST}/authorize`,
    tokenEndpoint: `${process.env.REACT_APP_AUTH_HOST}/token`,
    logoutEndpoint: `${process.env.REACT_APP_AUTH_HOST}/endsession`,
    logoutRedirect: `${process.env.REACT_APP_AUTH_LOGOUT_URI}`,
    redirectUri: `${process.env.REACT_APP_AUTH_REDIRECT_URI}`,
    scope: authScopes.toString().replace(/,/g, ' '),
    decodeToken: false,
    autoLogin: false,
    storage: 'session',
    preLogin: () => sessionStorage.setItem('lastPath', window.location.pathname === '/login' ? '/' : window.location.pathname),
};

const UKPBAuthContext = React.createContext<UKPBAuthProviderContext>({} as UKPBAuthProviderContext);

const UKPBAuthProvider = (props: any) => {
    const navigate = useNavigate();

    const {
        login,
        logOut,
        loginInProgress,
        error,
        token,
        tokenData,
        idToken,
        idTokenData
    } = useContext(AuthContext);

    const loggingIn = useRef<boolean>(false);
    const loggingOut = useRef<boolean>(false);

    const LogIn = () => {
        if(!loginInProgress) {
            loggingIn.current = true;
            loggingOut.current = false;
            login();
        }
    }


    const LogOut = (forceClear?: boolean) => {
        loggingOut.current = true;
        if(forceClear) {
            ClearSessionStore();
        }
        logOut();
    }

    const LogInSuccess = () => {
        loggingIn.current = false;
        navigate(sessionStorage.getItem('lastPath') || '/');
    }

    const RedirectError = () => {
        ClearSessionStore();
        navigate(sessionStorage.getItem('lastPath') || '/');
    }

    const ClearSessionStore = () => {
        if(!loginInProgress && !loggingIn.current && !loggingOut.current) {
            sessionStorage.removeItem('ROCP_token');
            sessionStorage.removeItem('ROCP_idToken');
            sessionStorage.removeItem('ROCP_loginInProgress');
            sessionStorage.removeItem('ROCP_refreshToken');
            sessionStorage.removeItem('ROCP_refreshTokenExpire');
            sessionStorage.removeItem('ROCP_token');
            sessionStorage.removeItem('ROCP_tokenExpire');
        }
    }

    const ClearLocalStore = () => {
        if(!loginInProgress && !loggingIn.current && !loggingOut.current) {
            localStorage.removeItem('ROCP_idToken');
            localStorage.removeItem('ROCP_loginInProgress');
            localStorage.removeItem('ROCP_refreshToken');
            localStorage.removeItem('ROCP_refreshTokenExpire');
            localStorage.removeItem('ROCP_token');
            localStorage.removeItem('ROCP_tokenExpire');
        }
    }

    useEffect(() => {
        ClearLocalStore();
        window.addEventListener("beforeunload", ClearSessionStore);
        return () => window.removeEventListener("beforeunload", ClearSessionStore);
    }, []);

    const authMemo = useMemo(() => {
        return {
            LogIn,
            LogOut,
            LogInSuccess,
            RedirectError,
            ClearSessionStore,
            error,
            token,
            loginInProgress,
            loggingIn,
            loggingOut,
            tokenData,
            idToken,
            idTokenData
        }
    }, [
        token,
        tokenData,
        idToken,
        idTokenData,
        error,
        loginInProgress,
        loggingIn,
        loggingOut
    ]);

    return (
        <UKPBAuthContext.Provider value={authMemo}>
            {props.children}
        </UKPBAuthContext.Provider>
    )
}

const useUKPBAuth = () => useContext(UKPBAuthContext);

export { UKPBAuthContext, useUKPBAuth };

export default UKPBAuthProvider
